'use client';

import classNames from 'classnames';
import { usePathname, useRouter } from 'next/navigation';
import type { ReactNode } from 'react';

import Footer from '~/shared/components/footer';
import Header from '~/shared/components/header';
import { mediaStyles } from '~/shared/components/media-query';
import RouteChangeListener from '~/shared/components/route-change-listener';
import RumInit from '~/shared/components/rum-init';
import WebClientProviders from '~/shared/components/web-client-providers';
import { getUserMenuItems } from '~/shared/configs/menus';
import {
  checkIsAdminPanel,
  checkIsClientsPanel,
  checkIsNoLayout,
  checkIsPlatform,
} from '~/shared/helpers/check-helpers';
import { getClientUser } from '~/shared/helpers/client-user-helpers';
import { useTypedTranslation } from '~/shared/hooks';
import useColorScheme from '~/shared/hooks/use-color-scheme';
import { isProduction, LOCALES, type Locale } from '~/universal/constants';

import './web-layout.scss';

const getLocaleFromPathname = (pathname: string | null, fallback: Locale): Locale => {
  if (!pathname) {
    return fallback;
  }

  const firstSegment = pathname.split('/')[1];

  if (Object.values(LOCALES).includes(firstSegment as Locale)) {
    return firstSegment as Locale;
  }

  return fallback;
};

type LayoutProps = Readonly<{
  lng: Locale;
  isNoFooter?: boolean;
  isStorage?: boolean;
  children: ReactNode;
}>;

const WebLayout = ({ isStorage = false, lng, children }: LayoutProps) => {
  useColorScheme();

  const router = useRouter();
  const t = useTypedTranslation(['page', 'text']);
  const pathname = usePathname();
  const effectiveLng = getLocaleFromPathname(pathname, lng);

  const { isNoLayout, isNoFooter } = checkIsNoLayout(pathname);
  const user = getClientUser();
  const isAuthenticated = Boolean(user);
  const isAdminPanel = checkIsAdminPanel(pathname);
  const isPlatform = checkIsPlatform(pathname);
  const isClientsPanel = checkIsClientsPanel(pathname);
  const menuItems = getUserMenuItems({
    isAuthenticated,
    rootContext: {},
    lng: effectiveLng,
    user,
    isClientsPanel,
    t,
    router,
    isPlatform,
    isAdminPanel,
  });
  const isLightPublicInfoPage = ['/about-us', '/contacts', '/privacy-policy', '/our-statute'].some((pagePath) =>
    pathname?.endsWith(pagePath),
  );

  return (
    <WebClientProviders lng={effectiveLng} user={user} isStorage={isStorage}>
      <div
        className={classNames('web-layout', {
          'web-layout--light-public-info-page': isLightPublicInfoPage,
        })}
      >
        <style type="text/css" dangerouslySetInnerHTML={{ __html: mediaStyles }} />
        <Header
          isPlatform={isPlatform}
          isAdminPanel={isAdminPanel}
          isClientsPanel={isClientsPanel}
          lng={effectiveLng}
          menuItems={menuItems}
        />
        {isProduction && <RumInit />}
        {isProduction && <RouteChangeListener />}
        <div className="web-layout__content">{children}</div>
        {!isNoFooter && <Footer lng={effectiveLng} />}
      </div>
    </WebClientProviders>
  );
};

export default WebLayout;
